// src/packages/care/prediction/orchestrator.ts
//
// The single entry point for a fertile-window prediction. Adapters (registry.ts) only declare
// eligibility and produce raw predictions; the orchestrator owns SELECTION and the final,
// cross-cutting result policy, then returns one FertileWindowPrediction for the UI (wired in
// Step 6 via useFertileWindowPrediction).
//
// Selection: walk the registry in priority order and use the first eligible model
//   • ≥2 usable cycles → personalized Bayesian model
//   • exactly 1 cycle  → calendar baseline
//   • 0 cycles / 0 starts → no model eligible → a clean insufficient_data result (never a crash
//     or a fabricated number)
//
// Notes on scope:
//   • The "not a contraceptive method" disclaimer is a STATIC UI element (already rendered by the
//     calendar in Phase 2), not a per-prediction warning — so it is intentionally NOT added to
//     `warnings` here, to avoid double-showing. `warnings` carries only situational caveats
//     (limited/irregular history) surfaced by the selected model.
//   • Specialist signals (LH/BBT/mucus/wearable) will slot in as additional registry models once
//     their backend schema exists (issue #1953); selection here does not change.

import { FERTILE_WINDOW_MODELS } from "./adapters/registry";
import { KNOWLEDGE_BASE_VERSION } from "./knowledgeBase";
import { getPeriodStartDates } from "./evidenceMapper";
import type { PatientEvidence } from "./evidence.types";
import type {
  FertileWindowPrediction,
  LocalDate,
  PredictionWarning,
} from "./fertileWindow.types";

function dedupe(values: PredictionWarning[]): PredictionWarning[] {
  return [...new Set(values)];
}

/** Result when no model is eligible (0 usable cycles) — educational/insufficient state. */
function insufficientResult(
  evidence: PatientEvidence,
): FertileWindowPrediction {
  // Same selector the models use, so the count matches the cycle-stat view (deduped starts).
  const periodStartCount = getPeriodStartDates(evidence).length;

  return {
    status: "insufficient_data",
    nextPeriod: null,
    ovulation: null,
    fertileDays: [],
    confidence: "low",
    modelId: "none",
    modelVersion: `kb.${KNOWLEDGE_BASE_VERSION}`,
    evidenceUsed: [
      {
        type: "period_start",
        observationCount: periodStartCount,
        status: "ignored",
        note:
          periodStartCount > 0
            ? "not enough cycles yet to estimate a fertile window"
            : "no logged periods",
      },
    ],
    warnings: [],
  };
}

/**
 * Resolve the single fertile-window prediction for a patient from all available evidence.
 * This is the package's public entry point (the Step 6 hook calls this).
 */
export function getFertileWindowPrediction(
  evidence: PatientEvidence,
  today: LocalDate,
): FertileWindowPrediction {
  const model = FERTILE_WINDOW_MODELS.find((m) => m.isEligible(evidence));

  if (!model) {
    return insufficientResult(evidence);
  }

  const prediction = model.predict(evidence, today);

  // Final normalization: de-duplicate situational warnings. Status/confidence already follow
  // the shared DATA_SUFFICIENCY policy (every model reads the same knowledge base), so no
  // cross-model re-downgrade is needed here.
  return { ...prediction, warnings: dedupe(prediction.warnings) };
}
